import React, { useState, useEffect, useRef } from 'react';

interface BatteryDataPoint {
  timestamp: string;
  vin: number | null;
  vbat: number | null;
}

interface BatteryHealthModalProps {
  isOpen: boolean;
  onClose: () => void;
  unitId: string;
  vehicleName: string;
  platform: 'camel' | 'starlink';
}

const BatteryHealthModal: React.FC<BatteryHealthModalProps> = ({
  isOpen, onClose, unitId, vehicleName, platform
}) => {
  const [data, setData] = useState<BatteryDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [systemType, setSystemType] = useState<'12V' | '24V'>('12V');
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; data: BatteryDataPoint } | null>(null);
  const [scrollPosition, setScrollPosition] = useState(100);
  const [visibleRange, setVisibleRange] = useState(500);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const thresholds = {
    '12V': { danger: 11.5, warning: 12.5, good: 13.8, max: 14.8, overcharge: 15.0, vinMax: 16 },
    '24V': { danger: 23.0, warning: 25.0, good: 27.2, max: 28.8, overcharge: 29.5, vinMax: 32 }
  };

  useEffect(() => {
    if (isOpen && unitId) fetchBatteryHistory();
  }, [isOpen, unitId, platform]);

  useEffect(() => {
    if (data.length > 0 && canvasRef.current) drawChart();
  }, [data, systemType, scrollPosition, visibleRange]);

  const fetchBatteryHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('clientToken');
      const endpoint = platform === 'camel' 
        ? `https://api.camel.optitrac.com/api/camel/battery-history/${unitId}?days=30`
        : `https://api.camel.optitrac.com/api/starlink/battery-history/${unitId}`;
      
      const response = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setSystemType(result.systemType || '12V');
        setScrollPosition(100);
      }
    } catch (error) {
      console.error('Error fetching battery history:', error);
    }
    setLoading(false);
  };

  const getVisibleData = () => {
    if (data.length <= visibleRange) return data;
    const endIndex = Math.floor((scrollPosition / 100) * data.length);
    const startIndex = Math.max(0, endIndex - visibleRange);
    return data.slice(startIndex, Math.min(endIndex, data.length));
  };

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const visibleData = getVisibleData();
    if (visibleData.length === 0) return;

    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 40, right: 20, bottom: 60, left: 55 };
    const chartWidth = width - padding.left - padding.right;
    
    // Split chart: top 65% for VIN, bottom 35% for VBAT
    const vinChartHeight = (height - padding.top - padding.bottom) * 0.62;
    const vbatChartHeight = (height - padding.top - padding.bottom) * 0.32;
    const gapHeight = (height - padding.top - padding.bottom) * 0.06;
    const vinTop = padding.top;
    const vbatTop = vinTop + vinChartHeight + gapHeight;

    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, height);

    const t = thresholds[systemType];
    const vinMax = t.vinMax;
    const vbatMax = 5;

    const vinToY = (v: number) => vinTop + vinChartHeight - (v / vinMax) * vinChartHeight;
    const vbatToY = (v: number) => vbatTop + vbatChartHeight - (v / vbatMax) * vbatChartHeight;
    const timeToX = (i: number) => padding.left + (i / Math.max(1, visibleData.length - 1)) * chartWidth;

    // VIN chart background zones
    ctx.fillStyle = 'rgba(239, 68, 68, 0.15)';
    ctx.fillRect(padding.left, vinToY(t.danger), chartWidth, vinToY(0) - vinToY(t.danger));
    ctx.fillStyle = 'rgba(251, 191, 36, 0.12)';
    ctx.fillRect(padding.left, vinToY(t.warning), chartWidth, vinToY(t.danger) - vinToY(t.warning));
    ctx.fillStyle = 'rgba(34, 197, 94, 0.1)';
    ctx.fillRect(padding.left, vinToY(t.max), chartWidth, vinToY(t.good) - vinToY(t.max));
    ctx.fillStyle = 'rgba(239, 68, 68, 0.15)';
    ctx.fillRect(padding.left, vinToY(vinMax), chartWidth, vinToY(t.overcharge) - vinToY(vinMax));

    // VBAT chart background
    ctx.fillStyle = 'rgba(22, 101, 52, 0.08)';
    ctx.fillRect(padding.left, vbatTop, chartWidth, vbatChartHeight);

    // Grid lines and labels - VIN
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.font = '10px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    
    const vinSteps = systemType === '24V' ? [0, 8, 16, 24, 32] : [0, 4, 8, 12, 16];
    ctx.fillStyle = '#dc2626';
    vinSteps.forEach(v => {
      const y = vinToY(v);
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText(`${v}V`, padding.left - 6, y + 3);
    });

    // Grid lines and labels - VBAT
    ctx.fillStyle = '#166534';
    [0, 2.5, 5].forEach(v => {
      const y = vbatToY(v);
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillText(`${v}V`, padding.left - 6, y + 3);
    });

    // X-axis labels
    ctx.fillStyle = '#666';
    ctx.font = '9px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    const labelCount = Math.min(10, visibleData.length);
    const labelInterval = Math.max(1, Math.floor(visibleData.length / labelCount));
    
    visibleData.forEach((d, i) => {
      if (i % labelInterval === 0 || i === visibleData.length - 1) {
        const x = timeToX(i);
        const date = new Date(d.timestamp);
        ctx.fillText(`${date.getDate()}/${date.getMonth() + 1}`, x, height - padding.bottom + 14);
        ctx.fillText(`${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`, x, height - padding.bottom + 26);
      }
    });

    // Draw VIN line (red)
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    let started = false;
    visibleData.forEach((d, i) => {
      if (d.vin !== null && !isNaN(d.vin)) {
        const x = timeToX(i);
        const y = vinToY(Math.min(d.vin, vinMax));
        if (!started) { ctx.moveTo(x, y); started = true; }
        else ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw VBAT line (dark green)
    ctx.strokeStyle = '#166534';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    started = false;
    visibleData.forEach((d, i) => {
      if (d.vbat !== null && !isNaN(d.vbat)) {
        const x = timeToX(i);
        const y = vbatToY(Math.min(d.vbat, vbatMax));
        if (!started) { ctx.moveTo(x, y); started = true; }
        else ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Labels
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#dc2626';
    ctx.fillText(`VIN - Vehicle Battery (${systemType})`, padding.left, vinTop - 8);
    ctx.fillStyle = '#166534';
    ctx.fillText('VBAT - Device Battery (0-5V)', padding.left, vbatTop - 8);

    ctx.fillStyle = '#888';
    ctx.textAlign = 'right';
    ctx.fillText(`${data.length} records`, width - padding.right, 16);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const visibleData = getVisibleData();
    if (visibleData.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const x = (e.clientX - rect.left) * scaleX;
    const padding = { left: 55, right: 20 };
    const chartWidth = canvas.width - padding.left - padding.right;

    if (x >= padding.left && x <= canvas.width - padding.right) {
      const dataIndex = Math.round(((x - padding.left) / chartWidth) * (visibleData.length - 1));
      if (dataIndex >= 0 && dataIndex < visibleData.length) {
        setHoveredPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top, data: visibleData[dataIndex] });
        return;
      }
    }
    setHoveredPoint(null);
  };

  if (!isOpen) return null;

  const visibleData = getVisibleData();
  const firstDate = visibleData[0]?.timestamp ? new Date(visibleData[0].timestamp).toLocaleString('en-NZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  const lastDate = visibleData[visibleData.length - 1]?.timestamp ? new Date(visibleData[visibleData.length - 1].timestamp).toLocaleString('en-NZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '14px', width: '95%', maxWidth: '1100px', maxHeight: '95vh', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>🔋 Battery Health</h2>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>{vehicleName} • {unitId} • {platform.toUpperCase()}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666' }}>×</button>
        </div>

        {/* Chart */}
        <div style={{ padding: '16px', position: 'relative' }}>
          {loading ? (
            <div style={{ height: '380px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading...</div>
          ) : data.length === 0 ? (
            <div style={{ height: '380px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No data available</div>
          ) : (
            <>
              {data.length < 100 && (
                <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', color: '#92400e' }}>
                  ⚠️ Limited data available ({data.length} records). {platform === 'starlink' ? 'Starlink API only provides recent events.' : 'Vehicle may have limited transmission history.'}
                </div>
              )}
              <canvas ref={canvasRef} width={1050} height={380} style={{ width: '100%', height: 'auto', cursor: 'crosshair' }}
                onMouseMove={handleCanvasMouseMove} onMouseLeave={() => setHoveredPoint(null)} />
              
              {hoveredPoint && (
                <div style={{ position: 'absolute', left: Math.min(hoveredPoint.x + 10, 850), top: hoveredPoint.y - 60,
                  backgroundColor: 'rgba(0,0,0,0.9)', color: 'white', padding: '8px 12px', borderRadius: '6px', fontSize: '11px', pointerEvents: 'none', zIndex: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>{new Date(hoveredPoint.data.timestamp).toLocaleString('en-NZ')}</div>
                  <div style={{ color: '#fca5a5' }}>VIN: {hoveredPoint.data.vin?.toFixed(2) || 'N/A'}V</div>
                  <div style={{ color: '#86efac' }}>VBAT: {hoveredPoint.data.vbat?.toFixed(2) || 'N/A'}V</div>
                </div>
              )}

              {/* Controls */}
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', fontSize: '11px', color: '#666' }}>
                  <span>{firstDate} → {lastDate} ({visibleData.length} of {data.length})</span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ color: '#888' }}>Points:</span>
                    {[100, 250, 500, 1000, 2000].map(n => (
                      <button key={n} onClick={() => setVisibleRange(n)} style={{
                        padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer',
                        background: visibleRange === n ? '#2563eb' : '#f0f0f0', color: visibleRange === n ? '#fff' : '#555'
                      }}>{n}</button>
                    ))}
                    <button onClick={() => { setVisibleRange(data.length); setScrollPosition(100); }} style={{
                      padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: visibleRange >= data.length ? '#2563eb' : '#f0f0f0', color: visibleRange >= data.length ? '#fff' : '#555'
                    }}>All</button>
                  </div>
                </div>
                <input type="range" min="0" max="100" value={scrollPosition} onChange={(e) => setScrollPosition(Number(e.target.value))}
                  style={{ width: '100%', cursor: 'pointer', height: '6px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#aaa', marginTop: '2px' }}>
                  <span>← Older</span><span>Newer →</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Legend */}
        <div style={{ padding: '10px 20px', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '10px' }}>
          <div><span style={{ color: '#dc2626', fontWeight: 600 }}>● Danger</span> {systemType === '12V' ? '<11.5V' : '<23V'}</div>
          <div><span style={{ color: '#f59e0b', fontWeight: 600 }}>● Warning</span> {systemType === '12V' ? '11.5-12.5V' : '23-25V'}</div>
          <div><span style={{ color: '#22c55e', fontWeight: 600 }}>● Good</span> {systemType === '12V' ? '13.8-14.8V' : '27.2-28.8V'}</div>
          <div><span style={{ color: '#dc2626', fontWeight: 600 }}>● Overcharge</span> {systemType === '12V' ? '>15V' : '>29.5V'}</div>
        </div>
      </div>
    </div>
  );
};

export default BatteryHealthModal;
